import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BaseHttpService } from '@core/http/base-http.service';
import { CommunityDeltaPayload } from '@shared/interfaces/community.interface';
import { EventQueryFormSubmission } from '@shared/interfaces/event-query-form.interface';
import {
  EventInterface,
  EventStatistic,
} from '@shared/interfaces/event.interface';
import {
  JoinInRequestInterface,
  JoinState,
} from '@shared/interfaces/join.interface';
import { Observable, of } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class EventHttpService extends BaseHttpService<EventInterface> {
  constructor(protected http: HttpClient) {
    super(http);
  }

  getApiRootPath(): string {
    return 'events';
  }

  join(id: number, dto: JoinInRequestInterface): Observable<any> {
    if (id) {
      let url = `${this.apiUrl}/${id}/participants/join`;
      if (dto?.targetTimezone !== null && !Number.isNaN(dto.targetTimezone)) {
        url += `?targetTimezone=${dto.targetTimezone}`;
      }
      return this.http.post(url, dto);
    } else {
      return of(undefined);
    }
  }

  getJoinStates(ids: string[]): Observable<JoinState[]> {
    const url = `${this.apiUrl}/participants/check-join`;
    if (ids?.length > 0) {
      return this.http.get<JoinState[]>(url, { params: { ids } });
    }
    return of([]);
  }

  addToCalendar(id: number, params): Observable<HttpResponse<Blob>> {
    if (id) {
      const url = `${this.apiUrl}/${id}/calendar-file`;
      return this.http.get(url, {
        responseType: 'blob',
        observe: 'response',
        params,
      });
    } else {
      return of(null);
    }
  }

  removeMyself(myId: number): Observable<boolean> {
    const url = `${this.apiUrl}/${myId}/participants/remove-myself`;
    return this.http.post<boolean>(url, {});
  }

  removeMyselfAsPartnerOrg(id, params) {
    const url = `${this.apiUrl}/${id}/partner/remove-myself`;
    return this.http.post(url, params);
  }

  getPaymentToken(params = {}) {
    const url = `${this.apiUrl}/payment-token`;
    return this.http.get(url, { params });
  }

  getPaymentInfo(id) {
    const url = `${this.apiUrl}/${id}/payment`;
    return this.http.get(url);
  }

  checkout(id, params = {}) {
    const url = `${this.apiUrl}/${id}/payment-checkout`;
    return this.http.post(url, params);
  }

  getEventPaymentStatus(id, params) {
    const url = `${this.apiUrl}/${id}/event-payment-status`;
    return this.http.get(url, { params });
  }

  checkinParticipant(eventId: number, personId: number): Observable<void> {
    const url = `${this.apiUrl}/${eventId}/check-in`;

    return this.http.post<void>(url, { personId });
  }

  getParticipantQrCode(eventId: number): Observable<string> {
    const url = `${this.apiUrl}/${eventId}/my-ticket`;
    return this.http.get<string>(url);
  }

  updateEventCommunity(
    eventId: number,
    payload: CommunityDeltaPayload
  ): Observable<void> {
    const url = `${this.apiUrl}/${eventId}/update-event-community`;

    return this.http.post<void>(url, payload);
  }

  getParticipantVisibility(eventId: number): Observable<boolean> {
    const url = `${this.apiUrl}/${eventId}/check-participant-visible`;

    return this.http.get<boolean>(url);
  }

  getPrivateParticipantCount(eventId: number): Observable<number> {
    const url = `${this.apiUrl}/${eventId}/private-participant-count`;

    return this.http.get<number>(url);
  }

  checkCanSendMessage(eventId: number): Observable<boolean> {
    const url = `${this.apiUrl}/${eventId}/can-send-msg`;

    return this.http.get<boolean>(url);
  }

  checkCanViewDetail(eventId: number): Observable<boolean> {
    const url = `${this.apiUrl}/${eventId}/can-view-detail`;

    return this.http.get<boolean>(url);
  }

  increaseView(eventId: number, utmSource?: string): Observable<void> {
    const url = `${this.apiUrl}/${eventId}/track-view`;

    return this.http.post<void>(url, { utm_source: utmSource });
  }

  getEventStatistics(eventId): Observable<EventStatistic[]> {
    const url = `${this.apiUrl}/${eventId}/tracking-statistics`;

    return this.http.get<EventStatistic[]>(url);
  }

  getQueryForm(eventId: number): Observable<{ jsonForm: string }> {
    const url = `${this.apiUrl}/${eventId}/query-form`;

    return this.http.get<{ jsonForm: string }>(url);
  }

  saveQueryForm(eventId: number, jsonForm: string): Observable<void> {
    const url = `${this.apiUrl}/${eventId}/query-form`;

    return this.http.put<void>(url, { jsonForm });
  }

  deleteQueryForm(eventId: number): Observable<void> {
    const url = `${this.apiUrl}/${eventId}/query-form`;

    return this.http.delete<void>(url);
  }

  submitQueryForm(eventId: number, jsonFormData: string): Observable<void> {
    const url = `${this.apiUrl}/${eventId}/query-form/submit`;

    return this.http.post<void>(url, { jsonFormData });
  }

  getQueryFormSubmission(
    eventId: number,
    participantId?: number
  ): Observable<EventQueryFormSubmission> {
    const url = `${this.apiUrl}/${eventId}/query-form/submission`;
    const params = participantId ? { participantId } : {};

    return this.http.get<EventQueryFormSubmission>(url, { params });
  }

  submitQueryFormOnBehalf(
    eventId: number,
    participantId: number,
    jsonFormData: string
  ): Observable<void> {
    const url = `${this.apiUrl}/${eventId}/query-form/submit-on-behalf`;

    return this.http.post<void>(url, { participantId, jsonFormData });
  }
}
